import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';

const LAST_UPDATED = '9 de fevereiro de 2026';

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" aria-hidden />
              <h1 className="text-lg font-semibold text-foreground">Política de Privacidade</h1>
            </div>
            <Link to="/login">
              <Button variant="outline" size="sm" className="gap-1">
                <ArrowLeft className="h-4 w-4" />
                Voltar ao login
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-sm text-muted-foreground mb-8">
          Última atualização: {LAST_UPDATED}
        </p>

        <article className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-foreground">
          <section>
            <h2 className="text-xl font-semibold mb-2">1. Quem somos</h2>
            <p className="text-muted-foreground leading-relaxed">
              Esta política descreve como tratamos os dados dos usuários do sistema de cobranças e do aplicativo ZehMotoca, 
              utilizados no contexto de locação de motos e gestão financeira. O responsável pelo tratamento dos dados 
              é a empresa que opera o serviço Zeh Motoca (e, quando aplicável, a Corps Teknologi na qualidade de 
              desenvolvedora da plataforma), conforme identificado nos termos de uso ou no aplicativo.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">2. Dados que coletamos</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Podemos coletar e processar os seguintes tipos de dados:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
              <li><strong>Dados de cadastro e conta:</strong> nome, e-mail, CPF, telefone e dados de cliente/fornecedor vinculados ao contrato de locação.</li>
              <li><strong>Dados de uso do aplicativo:</strong> informações necessárias para login, perfil (incluindo foto de perfil, se fornecida) e preferências no app.</li>
              <li><strong>Localização:</strong> quando o aplicativo solicitar permissão, utilizamos a localização para registrar logs de acesso e melhorar a experiência do serviço.</li>
              <li><strong>Fotos e câmera:</strong> apenas se você autorizar, para definição de foto de perfil no aplicativo.</li>
              <li><strong>Identificadores do dispositivo:</strong> token de notificações push para envio de avisos (vencimentos, mensagens, atualizações do app).</li>
              <li><strong>Dados de uso do sistema web:</strong> acessos, ações no sistema e logs técnicos para segurança e operação.</li>
            </ul>
            <div className="mt-4 p-4 rounded-lg bg-muted/50 border border-border">
              <h3 className="text-sm font-semibold text-foreground mb-2">Permissões do aplicativo Android</h3>
              <p className="text-sm text-muted-foreground mb-2">
                O aplicativo ZehMotoca pode solicitar as seguintes permissões, sempre com seu consentimento:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                <li><strong>android.permission.CAMERA</strong> — usada apenas para tirar foto de perfil no app (opcional). Não gravamos vídeo nem usamos a câmera para outros fins.</li>
                <li><strong>Leitura de fotos/imagens</strong> — para que você possa escolher uma foto da galeria como foto de perfil (opcional).</li>
                <li><strong>Localização</strong> — para registrar logs de acesso e melhorar o serviço (quando autorizado).</li>
                <li><strong>Notificações push</strong> — para envio de avisos de vencimentos, mensagens e atualizações do app.</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                O uso da câmera e das fotos é opcional e só ocorre quando você decide alterar sua foto de perfil.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">3. Finalidade do tratamento</h2>
            <p className="text-muted-foreground leading-relaxed">
              Utilizamos os dados para: prestar e melhorar o serviço de cobranças e do aplicativo; identificar e autenticar usuários; 
              enviar notificações relevantes (boletos, vencimentos, novidades); cumprir obrigações legais e contratuais; 
              garantir a segurança da plataforma e dos usuários; e, quando aplicável, para fins de suporte e contato.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">4. Base legal e armazenamento</h2>
            <p className="text-muted-foreground leading-relaxed">
              O tratamento baseia-se na execução do contrato de locação e do uso do sistema, no consentimento (quando exigido, 
              por exemplo para notificações push ou uso de câmera/galeria) e no legítimo interesse para segurança e melhoria 
              do serviço. Os dados são armazenados em ambiente seguro pelo tempo necessário às finalidades descritas ou 
              conforme exigido por lei.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">5. Compartilhamento</h2>
            <p className="text-muted-foreground leading-relaxed">
              Os dados podem ser compartilhados com prestadores de serviço que auxiliam na operação da plataforma (hospedagem, 
              envio de notificações, suporte), sempre com obrigação de confidencialidade e em conformidade com a lei. 
              Não vendemos seus dados pessoais a terceiros.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">6. Seus direitos</h2>
            <p className="text-muted-foreground leading-relaxed">
              Você pode solicitar acesso, correção, exclusão ou portabilidade dos seus dados, além de revogar consentimentos 
              ou opor-se a determinados tratamentos, nos termos da LGPD e demais leis aplicáveis. Para exercer esses direitos 
              ou tirar dúvidas sobre esta política, entre em contato conosco pelos canais indicados no aplicativo ou no sistema 
              (por exemplo: telefone (84) 3026-3950 ou pelo próprio app/site).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">7. Alterações</h2>
            <p className="text-muted-foreground leading-relaxed">
              Podemos atualizar esta política de privacidade periodicamente. A data da última versão está indicada no topo 
              desta página. O uso continuado do sistema ou do aplicativo após alterações constitui aceite da nova versão, 
              quando aplicável.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">8. Contato</h2>
            <p className="text-muted-foreground leading-relaxed">
              Dúvidas sobre privacidade ou tratamento de dados: entre em contato pelo aplicativo ZehMotoca, pelo sistema web 
              ou pelo telefone (84) 3026-3950.
            </p>
          </section>
        </article>

        <div className="mt-10 pt-6 border-t border-border">
          <Link to="/login">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Voltar ao login
            </Button>
          </Link>
        </div>
      </main>

      <footer className="border-t border-border bg-muted/30 py-3 px-4 mt-auto">
        <div className="mx-auto max-w-3xl flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-sm text-muted-foreground">
          <span>Desenvolvido por Corps Teknologi</span>
          <span className="hidden sm:inline" aria-hidden>·</span>
          <Link to="/politica-de-privacidade" className="hover:underline">
            Política de Privacidade
          </Link>
        </div>
      </footer>
    </div>
  );
}
